import {Pinecast} from '@pinecast/common/icons';
import {LegalP} from '@pinecast/common/Text';
import {FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

type Props = {
  /**
   * The page knows that the tipper is signed in: it has their subscription to
   * the show. Without a subscription, the page cannot tell.
   */
  signedIn: boolean;
};

/**
 * Most people who open a tip jar have never heard of Pinecast, and are being
 * asked for a card. The mark and the link say who is behind the page.
 *
 * The note says what is true on every path. A tipper who is signed in goes
 * straight to Stripe, a subscriber who changes their amount confirms it in a
 * dialog and has no checkout, and only a tipper who is not signed in confirms
 * their email address first. It said "You'll confirm your email address before
 * checkout" to all of them.
 */
const TipJarFooter = ({signedIn}: Props) => {
  const css = useCSS();
  const designColors = useColors();

  return (
    <div
      className={css({
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        paddingTop: '12px',
        textAlign: 'center',
      })}
    >
      <a
        className={css({
          alignItems: 'center',
          color: designColors.contentText,
          display: 'inline-flex',
          gap: '9px',
          textDecoration: 'none',
        })}
        href="https://pinecast.com"
      >
        <Pinecast size={28} />
        <span
          className={css({
            fontFamily: 'Monument Grotesk Bold',
            fontSize: '17px',
            lineHeight: '24px',
          })}
        >
          {'Pinecast'}
        </span>
      </a>
      <LegalP style={{paddingRight: 0, textAlign: 'center'}}>
        {signedIn ? (
          <FormattedMessage
            description="Note at the foot of the tip jar page about how payment is taken, for a tipper who is signed in"
            defaultMessage="Payment is handled by Stripe."
          />
        ) : (
          <FormattedMessage
            description="Note at the foot of the tip jar page about how payment is taken, when the page does not know if the tipper is signed in. A tipper who is signed in goes straight to Stripe."
            defaultMessage="Payment is handled by Stripe. If you aren't signed in, you'll confirm your email address first."
          />
        )}
      </LegalP>
    </div>
  );
};

export default React.memo(TipJarFooter);
